import { ISelectMultiOption, SelectMulti } from '@salecycle/ui-component-library';
import { listOfStringsToMultiSelectOptions } from 'components/utils/getSelectOptions';
import { useState, useCallback, useEffect } from 'react';
import { TestingFilter } from './TestingFilter';
import { useOffsiteCampaignEditorState } from 'sections/offsite-campaigns/offsite-campaign-editor/context/UseOffsiteCampaignEditorContext';
import { useTranslation } from 'react-i18next';
import { ISegmentFilter } from 'sections/offsite-campaigns/entities/offsiteCampaign';
import { useBlocklistAllowlist } from 'sections/client-management/ui/pages/client-settings/blocklist-allowlist/components/hooks/useBlocklistAllowlist';
import {
  channelAll,
  IBlocklistAllowlistItem,
  listTypeAllowlist,
  recordTypeEmail,
} from 'sections/client-management/ui/pages/client-settings/blocklist-allowlist/entities/blocklist-allowlist-constants';
import { useRemoveSegmentFilter } from './hooks/useRemoveSegmentFilter';
import { useUpdateFilterList } from './hooks/useUpdateFilterList';

export interface EmailTestingFilterProps {
  segmentId: string;
  segmentFilter: ISegmentFilter;
}

const mapDataToSelectMultiOptions = (list: IBlocklistAllowlistItem[]): ISelectMultiOption<string>[] => {
  return list.map(e => {
    let label = `${e.name} <${e.value}>`;
    if (e.name === undefined) {
      label = e.value;
    }
    return {
      label,
      value: e.value,
    } as ISelectMultiOption<string>;
  });
};

export const EmailTestingFilter = ({ segmentId, segmentFilter }: EmailTestingFilterProps) => {
  const { t } = useTranslation();
  const { site } = useOffsiteCampaignEditorState();
  const queryString = `productType=${channelAll}&listType=${listTypeAllowlist}&recordType=${recordTypeEmail}`;
  const { data, isSuccess } = useBlocklistAllowlist(site.id, queryString);

  const [allowListOptions, setAllowListOptions] = useState<ISelectMultiOption<string>[]>([]);
  const [hasFocused, setHasFocused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const multiSelectOptions = listOfStringsToMultiSelectOptions(segmentFilter.options);

  useEffect(() => {
    if (isSuccess && data.length > 0) {
      setAllowListOptions(mapDataToSelectMultiOptions(data));
    }
  }, [isSuccess, data]);

  const deleteFilter = useRemoveSegmentFilter(segmentId, segmentFilter);
  const updateFilterList = useUpdateFilterList(segmentId, segmentFilter);

  const onElementFocused = useCallback(() => {
    setHasFocused(true);
    setIsDropdownOpen(true);
  }, []);

  const onElementBlur = useCallback(() => setIsDropdownOpen(false), []);

  return (
    <TestingFilter
      icon="envelope"
      title={t('campaigns:offsiteEditor.segments.filters.testing.name')}
      detail={t('campaigns:offsiteEditor.segments.filters.testing.detail')}
      tooltip={t('campaigns:offsiteEditor.segments.filters.delete.iconAriaLabel')}
      deleteButtonAriaLabel={t('campaigns:offsiteEditor.segments.filters.delete.iconAriaLabel')}
      deleteButtonTestId="delete-segment-filter-button"
      testId="accepted-urls"
      onRemove={deleteFilter}
    >
      <SelectMulti
        key="AcceptedURLs={true}"
        label={t('campaigns:offsiteEditor.segments.filters.testing.emailList.label')}
        ariaLabel={t('campaigns:offsiteEditor.segments.filters.testing.emailList.ariaLabel')}
        placeholder={t('campaigns:offsiteEditor.segments.filters.testing.emailList.placeholder')}
        creatable={false}
        options={allowListOptions}
        value={multiSelectOptions}
        onChange={updateFilterList}
        onFocus={onElementFocused}
        onBlur={onElementBlur}
        validationState={!segmentFilter.options.length && hasFocused && !isDropdownOpen ? 'warning' : null}
        validationMessage={
          !segmentFilter.options.length && hasFocused && !isDropdownOpen
            ? t('campaigns:offsiteEditor.segments.filters.testing.emailValidationMessage')
            : null
        }
        tooltipHeading={t('campaigns:offsiteEditor.segments.filters.testing.emailList.tooltipHeading')}
        tooltipDescription={t('campaigns:offsiteEditor.segments.filters.testing.emailList.tooltipDescription')}
      />
    </TestingFilter>
  );
};
